import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import BoldCheckMark from '../components/BoldCheckMark'
import { useTap } from '../hooks/useTap'
import LoadingSpinner from '../components/LoadingSpinner'
import { reviewKeywordApi, ReviewKeyword } from '../api/reviewKeyword'
import { pageCache } from '../utils/pageCache'
import { useReviewStore } from '../stores/reviewStore'
import toastUtil from '../utils/toast'
import ReviewKeywordSuggestBottomSheet from '../components/ReviewKeywordSuggestBottomSheet'

/** 키워드 목록 캐시 키 */
const KEYWORDS_CACHE_KEY = 'review-keywords'

/**
 * 키워드 한 줄.
 *
 * 줄 전체가 누르는 자리다. **표식(BoldCheckMark)은 손짓을 받지 않는다** —
 * 전에는 줄과 체크박스가 각자 반응해서, 체크박스 쪽 click 을
 * stopPropagation 으로 막아 두 번 처리되는 것을 겨우 피하고 있었다.
 * 누르는 일을 줄 하나로 모으면 그 장치가 필요 없다.
 *
 * 손 떼는 순간 처리한다(useTap). onClick 으로 두면 아이폰이 빠른 연속
 * 탭을 삼켜서, 빠르게 고를 때 탭이 먹지 않는다.
 */
const KeywordRow: React.FC<{
  name: string
  selected: boolean
  onToggle: () => void
}> = ({ name, selected, onToggle }) => {
  const tap = useTap(onToggle)
  return (
    <button
      type="button"
      {...tap}
      className="py-12px w-full flex items-center gap-12px cursor-pointer hover:opacity-70 text-left"
      aria-pressed={selected}
    >
      <BoldCheckMark checked={selected} size={24} />
      <span className="font-pretendard font-regular text-16px leading-20px text-textColorText2 text-left">
        {name}
      </span>
    </button>
  )
}

const ReviewKeywordSelect: React.FC = () => {
  const navigate = useNavigate()
  const { reviewKeywordIds: storeKeywordIds, setReviewKeywordIds } =
    useReviewStore()

  // 키워드 목록은 거의 바뀌지 않는다. 추천 화면에 갔다 돌아올 때
  // 다시 스피너를 띄우면 화면이 한 번 비어 보인다.
  const [keywords, setKeywords] = useState<ReviewKeyword[]>(
    () => pageCache.get<ReviewKeyword[]>(KEYWORDS_CACHE_KEY) ?? []
  )
  const [loading, setLoading] = useState(!pageCache.has(KEYWORDS_CACHE_KEY))
  const [selectedKeywordIds, setSelectedKeywordIds] = useState<Set<number>>(
    new Set(storeKeywordIds)
  )
  const [isSuggestOpen, setIsSuggestOpen] = useState(false)

  // 페이지 초기 로딩 시 리뷰 키워드 목록 불러오기
  useEffect(() => {
    const loadKeywords = async () => {
      try {
        if (!pageCache.has(KEYWORDS_CACHE_KEY)) {
          setLoading(true)
        }
        const response = await reviewKeywordApi.getKeywords()
        pageCache.set(KEYWORDS_CACHE_KEY, response.keywords)
        setKeywords(response.keywords)
      } catch (error) {
        console.error('리뷰 키워드 목록 불러오기 실패:', error)
      } finally {
        setLoading(false)
      }
    }

    loadKeywords()
  }, [])

  const handleKeywordToggle = (keywordId: number) => {
    setSelectedKeywordIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(keywordId)) {
        newSet.delete(keywordId)
      } else {
        newSet.add(keywordId)
      }
      return newSet
    })
  }

  const handleSubmit = () => {
    if (selectedKeywordIds.size === 0) {
      toastUtil.warning('키워드를 하나 이상 선택해주세요')
      return
    }

    // 선택된 리뷰 키워드를 스토어에 저장.
    // 이름도 함께 담는다 — 기록 화면에서 무엇을 골랐는지 요약해 보여준다.
    const keywordIdsArray = Array.from(selectedKeywordIds)
    const keywordNames = keywordIdsArray.map(
      (id) => keywords.find((k) => k.id === id)?.name ?? ''
    )
    setReviewKeywordIds(keywordIdsArray, keywordNames)

    // 완료 후 뒤로가기
    navigate(-1)
  }

  const isFormValid = selectedKeywordIds.size > 0

  const handleBack = () => {
    // 뒤로가기 (저장하지 않음)
    navigate(-1)
  }

  const renderKeywordOptions = () => {
    if (loading) {
      return (
        <div className="mt-16px w-full flex items-center justify-center py-40px">
          <LoadingSpinner size={28} borderWidth={4} />
        </div>
      )
    }

    return (
      <div className="mt-16px w-full flex flex-col">
        {keywords.map((keyword) => {
          return (
            <KeywordRow
              key={keyword.id}
              name={keyword.name}
              selected={selectedKeywordIds.has(keyword.id)}
              onToggle={() => handleKeywordToggle(keyword.id)}
            />
          )
        })}
      </div>
    )
  }

  const renderSubmitButton = () => {
    return (
      <SubmitButton
        text="완료"
        onSubmit={handleSubmit}
        disabled={!isFormValid}
        onDisabled={handleSubmit}
        addClassName="mt-20px"
      />
    )
  }

  const handleSuggest = () => {
    setIsSuggestOpen(true)
  }

  const renderSuggestActionButton = () => {
    return (
      <button
        type="button"
        onClick={handleSuggest}
        className="px-12px py-4px hover:opacity-70"
      >
        <span className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] text-center">
          추가
        </span>
      </button>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        {/* AppBar */}
        <AppBar
          title="키워드 선택"
          actionSize={54}
          isBottomBorder
          onBack={handleBack}
          rightElement={
            <>
              {/* 추가 버튼 */}
              {renderSuggestActionButton()}
            </>
          }
        />

        {/* 컨텐츠 영역 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pb-24px">
          {/* 키워드 옵션 */}
          {renderKeywordOptions()}
        </div>

        {/* 하단 버튼 */}
        <div className="px-20px">
          {renderSubmitButton()}
        </div>
      </div>

      {/* 키워드 추가(제안) 바텀시트 */}
      <ReviewKeywordSuggestBottomSheet
        isOpen={isSuggestOpen}
        onClose={() => setIsSuggestOpen(false)}
      />
    </div>
  )
}

export default ReviewKeywordSelect
